import React, { useEffect, useState, useCallback } from "react";
import { useNexauraData } from "../context/NexauraContext";
import { useWallet } from "../context/WalletContext";
import Card from "../components/common/Card";
import Button from "../components/common/Button";
import { fmtUsdt } from "../utils/format";

const ROYALTY_BOARDS = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function RoyaltyTab() {
  const { data, busy, claimRoyalty, fetchClaimableRoyalty, fetchEligibilityForBoard } = useNexauraData();
  const { refreshKey } = useWallet();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!data.userId) return setRows([]);
    setLoading(true);
    const out = [];
    for (const board of ROYALTY_BOARDS) {
      const elig = await fetchEligibilityForBoard(data.userId, board);
      const claimable = await fetchClaimableRoyalty(data.userId, board);
      out.push({ board, eligible: elig ? elig.royalty : false, claimable });
    }
    setRows(out);
    setLoading(false);
  }, [data.userId, fetchEligibilityForBoard, fetchClaimableRoyalty]);

  useEffect(() => { load(); }, [data.userId, refreshKey, busy]); // eslint-disable-line

  if (loading && rows.length === 0) return <Card><div className="empty">Loading royalty…</div></Card>;

  return (
    <>
      {rows.map((r) => {
        const has = r.claimable && !r.claimable.isZero?.();
        return (
          <Card key={r.board} glow={r.eligible ? "gold" : ""} className={`section-gap reward-card ${r.eligible ? "eligible" : "dim"}`}>
            <div className="flex-between">
              <div style={{ fontWeight: 800, fontSize: 15 }}>Board {r.board}</div>
              <span className={`badge ${r.eligible ? "elig" : "inelig"}`}>
                {r.eligible ? "✓ Eligible" : "Not eligible"}
              </span>
            </div>
            <div className="row">
              <span className="r-label">Claimable</span>
              <span className="r-value">{r.claimable ? fmtUsdt(r.claimable) : "0"} USDT</span>
            </div>
            <Button variant="secondary" disabled={!r.eligible || !has} loading={busy} onClick={() => claimRoyalty(r.board)}>
              Claim Board {r.board} Royalty
            </Button>
          </Card>
        );
      })}
    </>
  );
}

function ClubsTab() {
  const { data, busy, claimClub } = useNexauraData();
  const e = data.eligibility;
  const clubs = [
    { id: 0, name: "Gold Club", req: "Boards 3–5 active", elig: e.gold, claimable: data.claimableGold, glow: "gold" },
    { id: 1, name: "Diamond Club", req: "Boards 6–8 active", elig: e.diamond, claimable: data.claimableDiamond, glow: "blue" },
    { id: 2, name: "Global Club", req: "Boards 9–12 active", elig: e.global, claimable: data.claimableGlobal, glow: "blue" },
  ];
  return (
    <>
      {clubs.map((c) => (
        <Card key={c.id} glow={c.elig ? c.glow : ""} className={`section-gap reward-card ${c.elig ? "eligible" : "dim"}`}>
          <div className="flex-between">
            <div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>{c.name}</div>
              <div style={{ fontSize: 12, color: "var(--text-faint)" }}>{c.req}</div>
            </div>
            <span className={`badge ${c.elig ? "elig" : "inelig"}`}>{c.elig ? "✓ Eligible" : "Not yet"}</span>
          </div>
          <div className="row" style={{ marginTop: 8 }}>
            <span className="r-label">Claimable</span>
            <span className="r-value">{c.claimable ? fmtUsdt(c.claimable) : "0"} USDT</span>
          </div>
          <Button
            variant="secondary"
            disabled={!c.elig || !c.claimable || c.claimable.isZero?.()}
            loading={busy}
            onClick={() => claimClub(c.id)}
          >
            Claim {c.name}
          </Button>
        </Card>
      ))}
    </>
  );
}

export default function Rewards() {
  const [tab, setTab] = useState("royalty");
  return (
    <div className="screen">
      <div className="screen-title">Rewards</div>
      <div className="pill-tabs">
        <button className={tab === "royalty" ? "active" : ""} onClick={() => setTab("royalty")}>Royalty</button>
        <button className={tab === "clubs" ? "active" : ""} onClick={() => setTab("clubs")}>Clubs</button>
      </div>
      {tab === "royalty" ? <RoyaltyTab /> : <ClubsTab />}
    </div>
  );
}
